/**
 * SupportNudge Component
 * 
 * A flexible nudge system for promoting skips and tips.
 * Architected to support future mascot additions without refactor.
 * 
 * Usage:
 * - Static mode: For informational text on submit pages
 * - Animated mode: For one-time slide-in nudges on session pages
 */

import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { Zap } from 'lucide-react';

// Nudge messages for rotation (session page) - designed to drive skip purchases
// Keep messages SHORT to prevent text wrapping on mobile
const SESSION_NUDGE_MESSAGES = [
  { text: "Skip the line, get heard first", emoji: "🔥", cta: "Skip now →" },
  { text: "Support this reviewer to jump the line", emoji: "💰", cta: "Tip now →" },
  { text: "Priority placement available", emoji: "⚡", cta: "Upgrade →" }
];

/**
 * Static info nudge for Submit/Upload page
 * Non-animated, informational only
 */
export const SubmitPageNudge = ({ className = '' }) => {
  return (
    <div className={`flex items-center gap-2 text-sm text-gray-500 ${className}`}>
      <Zap className="h-3.5 w-3.5 text-yellow-500 flex-shrink-0" />
      <span>Choose how much feedback you want. More insight time gives you priority queue placement.</span>
    </div>
  );
};

/**
 * Animated one-time nudge for Session page
 * Slides in from bottom-right, auto-dismisses after 4 seconds
 * Shows only once per session (uses sessionStorage)
 * 
 * Props:
 * - skipsEnabled: boolean - Only show if skips are enabled
 * - hasActiveQueue: boolean - Only show if reviewer has submissions in queue
 * - sessionId: string - Used to track "shown once" per session
 * - onDismiss: function - Optional callback when nudge is dismissed
 * - mascot: ReactNode - Future: pass a mascot component to display alongside message
 */
export const SessionNudge = ({ 
  skipsEnabled = true, 
  hasActiveQueue = true, 
  sessionId,
  onDismiss,
  onAction, // New: callback when CTA is clicked
  // eslint-disable-next-line no-unused-vars
  mascot = null // Reserved for future mascot additions
}) => {
  const [isVisible, setIsVisible] = useState(false);
  const [isAnimatingOut, setIsAnimatingOut] = useState(false);
  const [message, setMessage] = useState(null);

  const handleDismiss = () => {
    setIsAnimatingOut(true);
    setTimeout(() => {
      setIsVisible(false);
      setIsAnimatingOut(false);
      if (onDismiss) onDismiss();
    }, 300);
  };

  const handleAction = () => {
    // Scroll to submit button or trigger action
    const submitBtn = document.querySelector('[data-testid="submit-music-btn"]');
    if (submitBtn) {
      submitBtn.scrollIntoView({ behavior: 'smooth', block: 'center' });
      submitBtn.classList.add('ring-2', 'ring-[#F2D857]', 'ring-offset-2');
      setTimeout(() => submitBtn.classList.remove('ring-2', 'ring-[#F2D857]', 'ring-offset-2'), 2000);
    }
    if (onAction) onAction();
    handleDismiss();
  };

  useEffect(() => {
    // Check if we should show the nudge
    if (!skipsEnabled || !hasActiveQueue || !sessionId) {
      return;
    }

    // Check if already shown this session
    const storageKey = `session_nudge_shown_${sessionId}`;
    const alreadyShown = sessionStorage.getItem(storageKey);
    
    if (alreadyShown) {
      return;
    }

    // Pick a random message
    const randomMessage = SESSION_NUDGE_MESSAGES[Math.floor(Math.random() * SESSION_NUDGE_MESSAGES.length)];

    // Show nudge after a brief delay (let page load first)
    const showTimer = setTimeout(() => {
      setMessage(randomMessage);
      setIsVisible(true);
      // Mark as shown
      sessionStorage.setItem(storageKey, 'true');
    }, 1500);

    // Auto-dismiss after 6 seconds of being visible (increased from 4s)
    const hideTimer = setTimeout(() => {
      setIsAnimatingOut(true);
      setTimeout(() => {
        setIsVisible(false);
        setIsAnimatingOut(false);
        if (onDismiss) onDismiss();
      }, 300);
    }, 7500); // 1.5s delay + 6s visible

    return () => {
      clearTimeout(showTimer);
      clearTimeout(hideTimer);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [skipsEnabled, hasActiveQueue, sessionId, onDismiss]);

  if (!isVisible && !isAnimatingOut) {
    return null;
  }

  // Responsive positioning: full-width bottom on mobile, fixed bottom-right on desktop
  const isMobile = typeof window !== 'undefined' && window.innerWidth < 640;

  const nudgeContent = (
    <div 
      style={{ 
        position: 'fixed',
        zIndex: 9998,  // Lower than HotSeatPrompt (z-50 = 50) to allow stacking
        bottom: '100px',  // Position ABOVE HotSeatPrompt (which is at bottom: 16px)
        left: isMobile ? '16px' : 'auto',
        right: '16px',
        width: isMobile ? 'calc(100vw - 32px)' : '380px',
        maxWidth: isMobile ? 'calc(100vw - 32px)' : '420px',
        transform: isVisible && !isAnimatingOut ? 'translateY(0)' : 'translateY(100%)',
        opacity: isVisible && !isAnimatingOut ? 1 : 0,
        transition: 'all 0.3s ease-out',
        pointerEvents: 'auto',
        contain: 'layout style',
        isolation: 'isolate'
      }}
      role="alert"
      aria-live="polite"
    >
      <div 
        style={{
          background: 'linear-gradient(135deg, #0a0a0a 0%, #1a1a1a 50%, #0a0a0a 100%)',
          color: 'white',
          padding: isMobile ? '16px 18px' : '18px 22px',
          borderRadius: '18px',
          boxShadow: '0 8px 32px rgba(242,216,87,0.25), 0 4px 16px rgba(0,0,0,0.4)',
          border: '1px solid rgba(242,216,87,0.35)',
          boxSizing: 'border-box'
        }}
      >
        {/* Main content row - using CSS Grid for bulletproof layout */}
        <div 
          style={{ 
            display: 'grid',
            gridTemplateColumns: '44px 1fr 32px',
            alignItems: 'center',
            gap: '12px',
            marginBottom: '12px'
          }}
        >
          {/* Animated icon */}
          {message && (
            <div 
              style={{ 
                background: '#F2D857', 
                borderRadius: '50%', 
                width: '40px',
                height: '40px',
                display: 'grid',
                placeItems: 'center',
                boxShadow: '0 2px 8px rgba(242,216,87,0.3)'
              }}
              className="animate-pulse"
            >
              <span style={{ fontSize: '20px' }}>{message.emoji}</span>
            </div>
          )}
          
          {/* Message text - single line with ellipsis if needed */}
          <p 
            style={{ 
              margin: 0,
              fontSize: isMobile ? '15px' : '16px',
              fontWeight: 600,
              lineHeight: 1.35,
              color: 'white',
              overflow: 'hidden',
              textOverflow: 'ellipsis'
            }}
          >
            {message?.text}
          </p>
          
          {/* Close button */}
          <button 
            onClick={(e) => { e.stopPropagation(); handleDismiss(); }}
            style={{
              color: '#666',
              background: 'transparent',
              border: 'none',
              padding: '4px',
              cursor: 'pointer',
              fontSize: '18px',
              display: 'grid',
              placeItems: 'center'
            }}
            aria-label="Dismiss"
          >
            ✕
          </button>
        </div>
        
        {/* CTA Button */}
        <button
          onClick={handleAction}
          style={{
            width: '100%',
            background: '#F2D857',
            color: 'black',
            fontWeight: 700,
            padding: '14px 20px',
            borderRadius: '14px',
            border: 'none',
            cursor: 'pointer',
            display: 'grid',
            gridTemplateColumns: '20px 1fr',
            alignItems: 'center',
            justifyItems: 'center',
            gap: '8px',
            fontSize: isMobile ? '15px' : '16px',
            minHeight: '48px',
            boxSizing: 'border-box'
          }}
        >
          <Zap style={{ width: '18px', height: '18px' }} />
          <span style={{ fontWeight: 700, justifySelf: 'start' }}>{message?.cta || "Get priority"}</span>
        </button>
      </div>
    </div>
  );

  // Use portal to render outside of any parent flex/layout context
  return createPortal(nudgeContent, document.body);
};

export default SessionNudge;
